<template>
    <div class="pagination-container">
        <!-- Results info -->
        <div class="pagination-info" v-if="showInfo">
            Showing {{ fromRow || 0 }} to {{ toRow || 0 }} of {{ totalRows || 0 }} results
        </div>

        <!-- Pagination controls -->
        <div class="pagination-controls" :class="{ 'justify-center': centered }">
            <!-- First page button -->
            <button @click="goToPage(1)" :disabled="currentPage === 1" class="pagination-btn first" title="First page">
                <span class="icon">⏮️</span>
            </button>

            <!-- Previous page button -->
            <button @click="goToPage(currentPage - 1)" :disabled="!hasPrevious" class="pagination-btn prev"
                title="Previous page">
                <span class="icon">◀</span>
                <span class="text" v-if="showText">Previous</span>
            </button>

            <!-- Page numbers -->
            <div class="page-numbers" v-if="showPageNumbers">
                <!-- First page -->
                <button v-if="showFirstPage" @click="goToPage(1)" class="page-number"
                    :class="{ active: currentPage === 1 }">
                    1
                </button>

                <!-- Ellipsis if needed -->
                <span v-if="showLeftEllipsis" class="ellipsis">...</span>

                <!-- Middle pages -->
                <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" class="page-number"
                    :class="{ active: currentPage === page }">
                    {{ page }}
                </button>

                <!-- Ellipsis if needed -->
                <span v-if="showRightEllipsis" class="ellipsis">...</span>

                <!-- Last page -->
                <button v-if="showLastPage" @click="goToPage(totalPages)" class="page-number"
                    :class="{ active: currentPage === totalPages }">
                    {{ totalPages }}
                </button>
            </div>

            <!-- Next page button -->
            <button @click="goToPage(currentPage + 1)" :disabled="!hasNext" class="pagination-btn next"
                title="Next page">
                <span class="text" v-if="showText">Next</span>
                <span class="icon">▶</span>
            </button>

            <!-- Last page button -->
            <button @click="goToPage(totalPages)" :disabled="currentPage === totalPages" class="pagination-btn last"
                title="Last page">
                <span class="icon">⏭️</span>
            </button>

            <!-- Rows per page selector -->
            <div class="rows-per-page" v-if="showRowsPerPage">
                <select v-model="localRowsPerPage" @change="onRowsPerPageChange">
                    <option v-for="option in rowsPerPageOptions" :key="option" :value="option">
                        {{ option }} per page
                    </option>
                </select>
            </div>
        </div>

        <!-- Go to page input -->
        <!-- <div class="go-to-page" v-if="showGoToPage">
      <span>Go to</span>
      <input 
        type="number" 
        v-model.number="goToPageInput" 
        @keyup.enter="goToPage(goToPageInput)"
        min="1"
        :max="totalPages"
      />
      <button @click="goToPage(goToPageInput)" class="go-btn">Go</button>
    </div> -->
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
// eslint-disable-next-line
const props = defineProps({
    // Pagination data from response model
    currentPage: {
        type: Number,
        required: true
    },
    totalPages: {
        type: Number,
        required: true
    },
    totalRows: {
        type: Number,
        required: true
    },
    rowsPerPage: {
        type: Number,
        default: 100
    },
    hasPrevious: {
        type: Boolean,
        required: true
    },
    hasNext: {
        type: Boolean,
        required: true
    },

    // Configuration options
    maxVisiblePages: {
        type: Number,
        default: 5
    },
    showInfo: {
        type: Boolean,
        default: true
    },
    showPageNumbers: {
        type: Boolean,
        default: true
    },
    showText: {
        type: Boolean,
        default: false
    },
    showRowsPerPage: {
        type: Boolean,
        default: true
    },
    showGoToPage: {
        type: Boolean,
        default: false
    },
    centered: {
        type: Boolean,
        default: false
    },
    rowsPerPageOptions: {
        type: Array,
        default: () => [10, 25, 50, 100, 250, 500]
    }
});
// eslint-disable-next-line
const emit = defineEmits(['page-changed', 'rows-per-page-changed']);

// Local state
const localRowsPerPage = ref(props.rowsPerPage);
const goToPageInput = ref(null);

// Computed properties
const fromRow = computed(() => {
    if (props.totalRows === 0) return 0;
    return ((props.currentPage - 1) * props.rowsPerPage) + 1;
});

const toRow = computed(() => {
    return Math.min(props.currentPage * props.rowsPerPage, props.totalRows);
});

const visiblePages = computed(() => {
    const maxVisible = props.maxVisiblePages;
    const current = props.currentPage;
    const total = props.totalPages;

    if (total <= maxVisible) {
        // Show all pages
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    // Calculate visible page range
    const halfVisible = Math.floor(maxVisible / 2);
    let start = Math.max(1, current - halfVisible);
    let end = Math.min(total, start + maxVisible - 1);

    // Adjust if at the end
    if (end === total) {
        start = Math.max(1, total - maxVisible + 1);
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const showFirstPage = computed(() => {
    return props.totalPages > props.maxVisiblePages && !visiblePages.value.includes(1);
});

const showLastPage = computed(() => {
    return props.totalPages > props.maxVisiblePages && !visiblePages.value.includes(props.totalPages);
});

const showLeftEllipsis = computed(() => {
    return showFirstPage.value && visiblePages.value[0] > 2;
});

const showRightEllipsis = computed(() => {
    return showLastPage.value && visiblePages.value[visiblePages.value.length - 1] < props.totalPages - 1;
});

// Methods
const goToPage = (page) => {
    if (!page) return;

    // Ensure page is within bounds
    page = Math.max(1, Math.min(parseInt(page), props.totalPages));

    if (page !== props.currentPage) {
        emit('page-changed', page);
    }

    // Clear go to page input
    goToPageInput.value = null;
};

const onRowsPerPageChange = () => {
    emit('rows-per-page-changed', localRowsPerPage.value);
};

// Watch for rowsPerPage prop changes
watch(() => props.rowsPerPage, (newValue) => {
    localRowsPerPage.value = newValue;
});
</script>

<style scoped>
.pagination-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.pagination-info {
    color: #666;
    font-size: 14px;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.pagination-controls.justify-center {
    justify-content: center;
}

.pagination-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #999;
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-btn .icon {
    font-size: 12px;
}

.page-numbers {
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0 4px;
}

.page-number {
    min-width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e0e0e0;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
}

.page-number:hover {
    background: #f5f5f5;
    border-color: #999;
}

.page-number.active {
    background: #1976d2;
    color: white;
    border-color: #1976d2;
}

.ellipsis {
    padding: 0 4px;
    color: #666;
}

.rows-per-page select {
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background: white;
    font-size: 14px;
    cursor: pointer;
    margin-left: 8px;
}

.rows-per-page select:focus {
    outline: none;
    border-color: #1976d2;
}

.go-to-page {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #666;
}

.go-to-page input {
    width: 60px;
    padding: 6px 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    text-align: center;
}

.go-to-page input:focus {
    outline: none;
    border-color: #1976d2;
}

.go-to-page .go-btn {
    padding: 6px 12px;
    background: #1976d2;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
}

.go-to-page .go-btn:hover {
    background: #1565c0;
}

/* Mobile responsive */
@media (max-width: 768px) {
    .pagination-controls {
        justify-content: center;
    }

    .pagination-btn .text {
        display: none;
    }

    .page-number {
        min-width: 32px;
        height: 32px;
    }
}
</style>